import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight } from "lucide-react";

export default function BookingPage() {
  const [models, setModels] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    scooter_model: "",
    issue_description: "",
    preferred_date: "",
    type: "service",
  });

  useEffect(() => {
    api.get("/models").then((r) => setModels(r.data));
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.scooter_model || !form.issue_description) {
      toast.error("Completeaza toate campurile obligatorii");
      return;
    }
    setLoading(true);
    try {
      await api.post("/reservations", form);
      setSubmitted(true);
      toast.success("Cererea ta a fost trimisa! Te contactam in scurt timp.");
    } catch (err) {
      toast.error("Eroare la trimitere. Suna-ne la 0743 110 277.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="booking-success" className="min-h-[70vh] flex items-center justify-center px-6">
        <div className="max-w-xl text-center">
          <div className="w-20 h-20 bg-[#00E5FF]/10 border border-[#00E5FF] flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-[#00E5FF]" />
          </div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase mb-4">Cerere trimisa!</h1>
          <p className="text-neutral-400 mb-8">
            Multumim, <span className="text-white">{form.name}</span>. Iti vom trimite un SMS de confirmare la <span className="text-[#00E5FF]">{form.phone}</span> si te contactam in maxim 24h pentru a stabili detaliile.
          </p>
          <button onClick={() => { setSubmitted(false); setForm({ name: "", phone: "", email: "", scooter_model: "", issue_description: "", preferred_date: "", type: "service" }); }} data-testid="new-booking-btn" className="inline-flex items-center gap-2 border border-white/20 hover:border-[#00E5FF] hover:text-[#00E5FF] px-6 py-3 uppercase tracking-wider text-sm transition-colors">
            Trimite alta cerere <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="booking-page" className="text-white">
      <section className="relative border-b border-neutral-900 py-16 px-6 lg:px-10">
        <div className="absolute inset-0 kk-grid-bg opacity-40" />
        <div className="relative max-w-7xl mx-auto">
          <div className="text-xs uppercase tracking-[0.3em] text-[#00E5FF] mb-4">// Programare online</div>
          <h1 className="font-display text-5xl sm:text-7xl uppercase leading-none">
            Trimite o <span className="text-[#00E5FF]">cerere.</span>
          </h1>
          <p className="text-neutral-400 mt-6 max-w-2xl">
            Spune-ne ce trotineta ai si ce s-a intamplat. Te sunam pentru programare si confirmare prin SMS.
          </p>
        </div>
      </section>

      <section className="py-16 px-6 lg:px-10 max-w-4xl mx-auto">
        <form onSubmit={submit} data-testid="booking-form" className="space-y-6 bg-neutral-950 border border-neutral-800 p-6 sm:p-10">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Nume *">
              <Input data-testid="booking-name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Ion Popescu" className="bg-neutral-900 border-neutral-800 focus:border-[#00E5FF] rounded-none" />
            </Field>
            <Field label="Telefon *">
              <Input data-testid="booking-phone" type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="0712 345 678" className="bg-neutral-900 border-neutral-800 focus:border-[#00E5FF] rounded-none" />
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Email (optional)">
              <Input data-testid="booking-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="numele@email.ro" className="bg-neutral-900 border-neutral-800 focus:border-[#00E5FF] rounded-none" />
            </Field>
            <Field label="Data preferata (optional)">
              <Input data-testid="booking-date" type="date" value={form.preferred_date} onChange={(e) => update("preferred_date", e.target.value)} className="bg-neutral-900 border-neutral-800 focus:border-[#00E5FF] rounded-none [color-scheme:dark]" />
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Model trotineta *">
              <Select value={form.scooter_model} onValueChange={(v) => update("scooter_model", v)}>
                <SelectTrigger data-testid="booking-model" className="bg-neutral-900 border-neutral-800 focus:border-[#00E5FF] rounded-none">
                  <SelectValue placeholder="Alege modelul Kukirin" />
                </SelectTrigger>
                <SelectContent className="bg-neutral-950 border-neutral-800 text-white">
                  {models.map((m) => (
                    <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>
                  ))}
                  <SelectItem value="Alt model">Alt model / nu stiu</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Tip cerere">
              <Select value={form.type} onValueChange={(v) => update("type", v)}>
                <SelectTrigger data-testid="booking-type" className="bg-neutral-900 border-neutral-800 focus:border-[#00E5FF] rounded-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-neutral-950 border-neutral-800 text-white">
                  <SelectItem value="service">Service / reparatie</SelectItem>
                  <SelectItem value="warranty">Garantie</SelectItem>
                  <SelectItem value="question">Intrebare</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Descrie problema *">
            <Textarea data-testid="booking-issue" value={form.issue_description} onChange={(e) => update("issue_description", e.target.value)} rows={5} placeholder="Ex: nu mai porneste, frana spate scartie, autonomia a scazut foarte mult..." className="bg-neutral-900 border-neutral-800 focus:border-[#00E5FF] rounded-none" />
          </Field>

          <Button type="submit" disabled={loading} data-testid="booking-submit-btn" className="w-full bg-[#00E5FF] hover:bg-[#5CEEFF] text-black font-semibold uppercase tracking-wider rounded-none h-12 text-sm">
            {loading ? "Se trimite..." : "Trimite cererea"}
          </Button>
          <p className="text-xs text-neutral-500 text-center">
            Vei primi un SMS de confirmare. Apoi te sunam pentru detalii.
          </p>
        </form>
      </section>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-wider text-neutral-400">{label}</Label>
    {children}
  </div>
);
